/**
 * Preferences Store
 * -----------------
 * Manages UI preferences like theme selection and avatars.
 * Persists to localStorage for instant UI, and syncs to progressStore
 * so preferences survive across devices via the API.
 */
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { useProgressStore } from './progressStore';

export const usePreferencesStore = defineStore('preferences', () => {
  const homeTheme = ref(localStorage.getItem('homeTheme') || 'standard');

  // Progress page theme ('garden' | future: 'dashboard', 'trophy')
  const progressTheme = ref(localStorage.getItem('progressTheme') || 'garden');

  // Avatar selections per student { studentId: emoji }
  const avatars = ref(JSON.parse(localStorage.getItem('avatars') || '{}'));

  // Theme class mapping for site-wide theming
  const themeClassMap = {
    'space-mission': 'theme-space',
    'mountain-trail': 'theme-trail',
    'road-map': 'theme-road',
  };

  // Sync homeTheme to localStorage and apply theme class to <html>
  watch(homeTheme, (val, oldVal) => {
    localStorage.setItem('homeTheme', val);
    if (typeof document !== 'undefined') {
      const oldClass = themeClassMap[oldVal];
      if (oldClass) document.documentElement.classList.remove(oldClass);
      const newClass = themeClassMap[val];
      if (newClass) document.documentElement.classList.add(newClass);
    }
  }, { immediate: true });

  /**
   * Set home page theme ('standard' | 'mountain-trail' | 'road-map' | 'space-mission')
   * @param {string} theme
   * @param {string} [childId] - if provided, also persists to progressStore
   */
  function setHomeTheme(theme, childId) {
    homeTheme.value = theme;
    if (childId) {
      const progressStore = useProgressStore();
      progressStore.setPreference(childId, { homeTheme: theme });
    }
  }

  function getAvatar(studentId) {
    return avatars.value[studentId] || null;
  }

  function setProgressTheme(theme) {
    progressTheme.value = theme;
    localStorage.setItem('progressTheme', theme);
  }

  function setAvatar(studentId, emoji) {
    avatars.value[studentId] = emoji;
    localStorage.setItem('avatars', JSON.stringify(avatars.value));
    if (studentId) {
      const progressStore = useProgressStore();
      progressStore.setPreference(studentId, { avatar: emoji });
    }
  }

  /**
   * Hydrate preferences from progressStore after progress is loaded.
   * Call this once after loadProgress completes for a child.
   */
  function hydrateFromProgress(childId) {
    const progressStore = useProgressStore();
    const prefs = progressStore.preferences[childId];
    if (!prefs) return;

    if (prefs.homeTheme) {
      homeTheme.value = prefs.homeTheme;
    }
    if (prefs.avatar) {
      avatars.value[childId] = prefs.avatar;
      localStorage.setItem('avatars', JSON.stringify(avatars.value));
    }
  }

  return {
    homeTheme,
    setHomeTheme,
    progressTheme,
    setProgressTheme,
    avatars,
    getAvatar,
    setAvatar,
    hydrateFromProgress
  };
});
